import IndexLayout from '../layouts';
import LazyLoad from 'react-lazyload';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import BackToTop from '../components/BackToTop';
import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';

import { graphql } from 'gatsby';
import Img from 'gatsby-image';

import ButtonLink from '../../src/components/ui/ButtonLink';
import ReactCompareImage from 'react-compare-image';
import Spinner from '../../src/components/ui/Spinner';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';

export const query = graphql`
  query {
    file(relativePath: { eq: "covers/capture-one-styles.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1800) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

interface CompareWrapperProps {
  thin?: boolean;
}

const CompareWrapper = styled.div<CompareWrapperProps>`
  max-width: ${(props) => (props.thin ? '450px' : '800px')};
  margin: 0 auto 3rem;
`;

class RerenderCompare extends React.Component {
  t1;
  state = { delayOver: false };

  componentDidMount() {
    this.t1 = setTimeout(() => {
      this.setState({ delayOver: true });
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.t1);
  }

  render() {
    if (!this.state.delayOver) {
      return <Spinner />;
    }
    return this.props.children();
  }
}

const PageTemplate = css`
  .header-image {
    margin-bottom: 4.5rem;
    box-shadow: 0 10px 20px -16px #382757;
  }

  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }

  h2::before {
    display: none !important;
  }
`;

const COImg = css`
  @media (max-width: 640px) {
    max-width: 140px;
  }
`;

const CaptureOneStyles: React.FunctionComponent = ({ data }) => {
  const [thanks, setThanks] = React.useState(false);

  function thankYouMessage() {
    setThanks(true);
  }

  return (
    <IndexLayout>
      <Helmet>
        <title>25 Free Capture One Styles | Giggster Guide</title>
        <meta
          name="description"
          content="25 absolutely free Capture One styles to instantly apply creative styles to your RAW photos. No opt-in needed, just download and use them right away."
        />

        <meta property="og:site_name" content="Giggster Guide" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="25 Free Capture One Styles" />
        <meta
          property="og:description"
          content="25 absolutely free Capture One styles to instantly apply creative styles to your RAW photos. No opt-in needed, just download and use them right away."
        />
        <meta property="og:url" content="https://giggster.com/guide/capture-one-styles/" />
        <meta property="og:image" content="https://giggster.com/guide/covers/capture-one-styles.jpg" />
        <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="25 Free Capture One Styles" />
        <meta
          name="twitter:description"
          content="25 absolutely free Capture One styles to instantly apply creative styles to your RAW photos. No opt-in needed, just download and use them right away."
        />
        <meta name="twitter:url" content="https://giggster.com/guide/capture-one-styles/" />
        <meta name="twitter:image" content="https://giggster.com/guide/covers/capture-one-styles.jpg" />
        <meta name="twitter:site" content="@Giggster" />
        <meta name="twitter:creator" content="@Giggster" />
      </Helmet>
      <Wrapper css={PageTemplate}>
        <header css={[outer, SiteHeader]}>
          <div css={inner}>
            <SiteNav />
          </div>
        </header>
        <main id="site-main" className="site-main" css={[SiteMain, outer]}>
          <article className="post page" css={[PostFull, NoImage]}>
            <PostFullHeader>
              <PostFullTitle>✨ 25 Free Capture One Styles</PostFullTitle>
              <h2 className="b3">
                Here I'm giving away the styles I created as part of my own Capture One workflow
              </h2>
              <Img className="header-image" fluid={data.file.childImageSharp.fluid} alt="" />
              {/*<ShareButtons
                title={'25 Free Capture One Styles'}
                slug={'/capture-one-styles/'}
                imgSrc={'covers/capture-one-styles.jpg'}
              />*/}
            </PostFullHeader>

            <PostFullContent className="post-full-content">
              <div className="post-content">
                <p>
                  Similar to how Lightroom presets work, styles in Capture One allow you to apply
                  many adjustments to your RAW photos all in one swoop. You can, therefore, give
                  your photo a specific creative look with only one click.
                </p>

                <p>
                  I've been doing a lot of my RAW editing in Capture One lately, and this has given
                  me the chance to create some interesting styles. Here I decided to share them for
                  free so that you can use them in your edits. For now, you'll find 25 Capture One
                  styles if you'd like to see
                  more added over time. These styles are compatible with the latest version of
                  Capture One (Capture One 20).
                </p>

                <p>
                  Some of these styles create quite a drastic effect, so don't hesitate to apply
                  them to a new layer instead and reduce the opacity of the style layer so that it
                  fits your photos just right. To learn more about styles in Capture One and how to
                  use them, <a href="/guide/retouching/using-styles-capture-one/">refer to this post</a>.
                </p>
                <p>
                  Finally, <a href="/guide/retouching/create-styles-capture-one/">here's another post</a>{' '}
                  I wrote if you're interested in learning how to create your own Capture One
                  styles.
                </p>
                <h2 className="text-center">&darr;</h2>
                <p className="text-center">
                  <ButtonLink
                    href="https://purple11.s3.amazonaws.com/capture-one-styles/capture-one-styles.zip"
                    download
                    onClick={thankYouMessage}
                  >
                    Download the Capture One styles
                  </ButtonLink>
                </p>
                {thanks ? (
                  <div>
                    <section
                      className="info-box text-center hidden"
                      style={{ border: '#F2EAF8 5px solid' }}
                    >
                      <p className="text-center t3">
                        Thank you for downloading! 🎉 Never stop creating! 📷
                      </p>
                    </section>
                    <hr />
                  </div>
                ) : (
                  ''
                )}
                <p className="text-center">
                  <small>
                    <a href="#instructions">Installation instructions</a>
                  </small>
                </p>
                

                <hr />
                <h2 className="text-center">Free Capture One Styles</h2>
                <p>
                  Here's a preview of the 25 free Capture One styles included in the pack. Drag the
                  slider on the images to get a before and after preview:
                </p>
                <h3 className="text-center">Bright</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/bright-before.jpg"
                          rightImage="/guide/images/capture-one-styles/bright-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A style for a bright, contrasty and highly saturated style look.
                </p>
                <hr />
                <h3 className="text-center">Cityscape</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/cityscape-before.jpg"
                          rightImage="/guide/images/capture-one-styles/cityscape-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A film-inspired style with film grain a magenta color tone.
                </p>
                <hr />
                <h3 className="text-center">Cowboy</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/cowboy-before.jpg"
                          rightImage="/guide/images/capture-one-styles/cowboy-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A very warm look throughout.</p>
                <hr />
                <h3 className="text-center">Denim</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/denim-before.jpg"
                          rightImage="/guide/images/capture-one-styles/denim-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Crisp and sharp with a blue tone.</p>
                <hr />
                <h3 className="text-center">Desert</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/desert-before.jpg"
                          rightImage="/guide/images/capture-one-styles/desert-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Contrasty and highly saturated. Most of the time you'll want to use that style on
                  a separate layer and reduce the opacity.
                </p>
                <hr />
                <h3 className="text-center">Exotic</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/exotic-before.jpg"
                          rightImage="/guide/images/capture-one-styles/exotic-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A vintage style with lower contrast and a matte feel.</p>
                <hr />
                <h3 className="text-center">Exotic 2</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/exotic-2-before.jpg"
                          rightImage="/guide/images/capture-one-styles/exotic-2-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A vintage style with a warm color grade.</p>
                <hr />
                <h3 className="text-center">Funky Nature</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/funky-nature-before.jpg"
                          rightImage="/guide/images/capture-one-styles/funky-nature-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A wild take on colors. This one will only work on very specific images.
                </p>
                <hr />
                <h3 className="text-center">Future Old School</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/future-old-school-before-2.jpg"
                          rightImage="/guide/images/capture-one-styles/future-old-school-after-2.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Intense, very high contrast and a cool neon-greenish vintage color grade.
                </p>
                <hr />
                <h3 className="text-center">Gloomy</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/gloomy-before.jpg"
                          rightImage="/guide/images/capture-one-styles/gloomy-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A strong vignette and greenish color grade.</p>
                <hr />
                <h3 className="text-center">High-Contrast Black and White</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/high-contrast-black-and-white-before.jpg"
                          rightImage="/guide/images/capture-one-styles/high-contrast-black-and-white-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Create very graphic black and white images with this style.
                </p>
                <hr />
                <h3 className="text-center">Intense Black and White</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/intense-black-and-white-before.jpg"
                          rightImage="/guide/images/capture-one-styles/intense-black-and-white-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A black and white style for very dark blues and cyans.
                </p>
                <hr />
                <h3 className="text-center">Power Black and White</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/power-black-and-white-before.jpg"
                          rightImage="/guide/images/capture-one-styles/power-black-and-white-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A black and white for light skin tones that pop.</p>
                <hr />
                <h3 className="text-center">Ultra Black and White</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/ultra-black-and-white-before.jpg"
                          rightImage="/guide/images/capture-one-styles/ultra-black-and-white-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Another take on a high-contrast black and white.</p>
                <hr />
                <h3 className="text-center">Vintage Black and White</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/vintage-black-and-white-before.jpg"
                          rightImage="/guide/images/capture-one-styles/vintage-black-and-white-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A high-contrast black and white with film grain.</p>
                <hr />
                <h3 className="text-center">Vintage Black and White 2</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/vintage-black-and-white-2-before.jpg"
                          rightImage="/guide/images/capture-one-styles/vintage-black-and-white-2-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Yet another take on black and white styling.</p>
                <hr />
                <h3 className="text-center">Nature</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/nature-before.jpg"
                          rightImage="/guide/images/capture-one-styles/nature-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  A green and contrasty style perfect for nature and vegetation.
                </p>
                <hr />
                <h3 className="text-center">New Day</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/new-day-before.jpg"
                          rightImage="/guide/images/capture-one-styles/new-day-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">
                  Bright and saturated. Perfect for those shots that have a lot of shadows to be
                  recovered.
                </p>
                <hr />
                <h3 className="text-center">Pop</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/pop-before.jpg"
                          rightImage="/guide/images/capture-one-styles/pop-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A saturated styles to make your images pop.</p>
                <hr />
                <h3 className="text-center">Soft Pink</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/soft-pink-before.jpg"
                          rightImage="/guide/images/capture-one-styles/soft-pink-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A slight pink grade.</p>
                <hr />
                <h3 className="text-center">Solitude</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/solitude-before.jpg"
                          rightImage="/guide/images/capture-one-styles/solitude-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A soft, contrasty and desaturated look.</p>
                <hr />
                <h3 className="text-center">W-01</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/w-01-before.jpg"
                          rightImage="/guide/images/capture-one-styles/w-01-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">A vintage brownish look.</p>
                <hr />
                <h3 className="text-center">W-02</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/w-02-before.jpg"
                          rightImage="/guide/images/capture-one-styles/w-02-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Another take on a vintage and saturated look.</p>
                <hr />
                <h3 className="text-center">Whoosh</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin={false}>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/whoosh-before.jpg"
                          rightImage="/guide/images/capture-one-styles/whoosh-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Crisp details and a mild HDR look.</p>
                <hr />
                <h3 className="text-center">Your Typical Color Grade</h3>
                <LazyLoad height={200}>
                  <CompareWrapper thin>
                    <RerenderCompare>
                      {() => (
                        <ReactCompareImage
                          sliderPositionPercentage={0.33}
                          leftImage="/guide/images/capture-one-styles/your-typical-color-grade-before.jpg"
                          rightImage="/guide/images/capture-one-styles/your-typical-color-grade-after.jpg"
                          sliderLineWidth={4}
                          skeleton={<Spinner />}
                        />
                      )}
                    </RerenderCompare>
                  </CompareWrapper>
                </LazyLoad>
                <p className="text-center">Blue/teal shadows with yellow/orange highlights.</p>
                <hr />
                <h2 id="instructions">Capture One Styles Installation Instructions</h2>
                <p>
                  Easy as pie! 🥧 Just unzip the downloaded file, and then in the unzipped folder
                  you'll find a file named <em>Purple-Capture-One-Styles.costylepack</em>. Double
                  click on that file and the style pack will be installed automatically into Capture
                  One.
                </p>
                <p>
                  You'll find them in the <em>Adjustments Tab</em> under <em>User Styles</em>:
                </p>
                <figure className="text-center screenshot">
                  <img
                    src="/guide/images/capture-one-styles/screens/capture-one-styles00.jpg"
                    alt="Screenshot of the styles panel in Capture One"
                  />
                </figure>
              </div>
            </PostFullContent>
          </article>
          <MoreGoodies slug="capture-one-styles" />
        </main>
        <BackToTop />
        <Footer />
      </Wrapper>
    </IndexLayout>
  );
};

export default CaptureOneStyles;
